"use client";

import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { layoutScale } from "@/lib/layout-size";
import { cn } from "@/lib/utils";

export type SwipeScrollFade = "none" | "right" | "both" | "auto";

export type SwipeScrollInitialScroll = "start" | "end";

/**
 * Edge fade for a horizontal scroller, as a mask on the scroller itself. The stop is a
 * fixed `2rem` rather than a percentage so the fade is the same width at every container
 * size, and it resolves against the element's own padding box — an absolutely positioned
 * overlay inside an `overflow-x-auto` box resolves `right-0` against the content width
 * instead, which parks it past the visible edge.
 */
export const SCROLL_FADE_CLASS: Record<
  Exclude<SwipeScrollFade, "auto">,
  string
> = {
  none: "",
  right:
    "[mask-image:linear-gradient(to_right,black_calc(100%_-_2rem),transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,black_calc(100%_-_2rem),transparent_100%)]",
  both: "[mask-image:linear-gradient(to_right,transparent_0%,black_2rem,black_calc(100%_-_2rem),transparent_100%)] [-webkit-mask-image:linear-gradient(to_right,transparent_0%,black_2rem,black_calc(100%_-_2rem),transparent_100%)]",
};

export interface SwipeScrollProps {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
  /**
   * Edge fade signalling that content continues. Default `"none"`.
   * `"auto"` is `"right"` but only while the content actually overflows — a
   * fixed fade on a scroller that fits promises content that is not there.
   */
  fade?: SwipeScrollFade;
  initialScroll?: SwipeScrollInitialScroll;
  initialScrollKey?: string | number;
}

export const SwipeScroll = forwardRef<HTMLDivElement, SwipeScrollProps>(
  function SwipeScroll(
    {
      children,
      className,
      style,
      fade = "none",
      initialScroll = "start",
      initialScrollKey,
    },
    ref,
  ) {
    const localRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => localRef.current as HTMLDivElement, []);
    const [overflows, setOverflows] = useState(false);

    useLayoutEffect(() => {
      if (initialScroll !== "end") return;
      const el = localRef.current;
      if (!el) return;

      let settledScrollLeft = -1;
      const maxScrollLeft = () => Math.max(0, el.scrollWidth - el.clientWidth);

      const pinToEnd = () => {
        const target = maxScrollLeft();
        if (Math.abs(el.scrollLeft - target) > 1) {
          el.scrollTo({ left: target, behavior: "instant" });
        }
        settledScrollLeft = el.scrollLeft;
      };

      const ro = new ResizeObserver(pinToEnd);
      const observeContent = () => {
        for (const child of Array.from(el.children)) ro.observe(child);
      };
      const mo = new MutationObserver(() => {
        observeContent();
        pinToEnd();
      });

      const release = () => {
        ro.disconnect();
        mo.disconnect();
        el.removeEventListener("scroll", onScroll);
      };

      const onScroll = () => {
        if (Math.abs(el.scrollLeft - settledScrollLeft) <= 1) return;
        if (maxScrollLeft() - el.scrollLeft <= 1) {
          settledScrollLeft = el.scrollLeft;
          return;
        }
        release();
      };

      pinToEnd();
      ro.observe(el);
      observeContent();
      mo.observe(el, { childList: true });
      el.addEventListener("scroll", onScroll, { passive: true });

      return release;
    }, [initialScroll, initialScrollKey]);

    useEffect(() => {
      if (fade !== "auto") return;
      const el = localRef.current;
      if (!el) return;

      // A fractional layout width leaves scrollWidth a sub-pixel above
      // clientWidth on a scroller that fits, which is not overflow.
      const read = () => setOverflows(el.scrollWidth - el.clientWidth > 1);
      read();

      // The scroller's own box does not resize when its content grows, so the
      // content has to be observed as well as the port.
      const ro = new ResizeObserver(read);
      ro.observe(el);
      for (const child of Array.from(el.children)) ro.observe(child);
      const mo = new MutationObserver(read);
      mo.observe(el, { childList: true, subtree: true, characterData: true });

      return () => {
        ro.disconnect();
        mo.disconnect();
      };
    }, [fade]);

    useEffect(() => {
      const el = localRef.current;
      if (!el) return;

      let isDown = false;
      let startX = 0;
      let startScrollLeft = 0;
      let dragScale = 1;
      let activePointerId: number | null = null;
      let moved = false;

      const onPointerDown = (e: PointerEvent) => {
        if (e.pointerType !== "mouse") return;
        if (e.button !== 0) return;
        isDown = true;
        moved = false;
        startX = e.clientX;
        startScrollLeft = el.scrollLeft;
        dragScale = layoutScale(el);
        activePointerId = e.pointerId;
      };

      const onPointerMove = (e: PointerEvent) => {
        if (!isDown || activePointerId !== e.pointerId) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 3) {
          if (!moved) {
            moved = true;
            try {
              el.setPointerCapture(e.pointerId);
            } catch {}
            el.style.cursor = "grabbing";
            el.style.userSelect = "none";
          }
          el.scrollLeft = startScrollLeft - dx / dragScale;
        }
      };

      const endDrag = (e: PointerEvent) => {
        if (activePointerId !== e.pointerId) return;
        isDown = false;
        activePointerId = null;
        el.style.cursor = "";
        el.style.userSelect = "";
        try {
          el.releasePointerCapture(e.pointerId);
        } catch {}
      };

      const onClickCapture = (e: MouseEvent) => {
        if (moved) {
          e.preventDefault();
          e.stopPropagation();
          moved = false;
        }
      };

      el.addEventListener("pointerdown", onPointerDown);
      el.addEventListener("pointermove", onPointerMove);
      el.addEventListener("pointerup", endDrag);
      el.addEventListener("pointercancel", endDrag);
      el.addEventListener("click", onClickCapture, true);
      return () => {
        el.removeEventListener("pointerdown", onPointerDown);
        el.removeEventListener("pointermove", onPointerMove);
        el.removeEventListener("pointerup", endDrag);
        el.removeEventListener("pointercancel", endDrag);
        el.removeEventListener("click", onClickCapture, true);
      };
    }, []);

    return (
      <div
        ref={localRef}
        className={cn(
          "overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          SCROLL_FADE_CLASS[
            fade === "auto" ? (overflows ? "right" : "none") : fade
          ],
          className,
        )}
        style={style}
      >
        {children}
      </div>
    );
  },
);
